import React, { useState } from 'react';
import { XIcon, ArrowRightIcon } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles, faEnvelope, faCartShopping, faPhone, faVideo, faTrophy, faBookOpen, faTag, faMoneyBill, faSackDollar, faGem, faEgg, faHammer, faBuilding, faStar, faBullseye } from '@fortawesome/free-solid-svg-icons';
import { FUNNEL_TYPES } from '../lib/data';
import { Button } from './ui/Button';
interface QuizRecommenderProps {
  isOpen: boolean;
  onClose: () => void;
  onRecommend: (typeId: string) => void;
}
export function QuizRecommender({
  isOpen,
  onClose,
  onRecommend
}: QuizRecommenderProps) {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    goal: '',
    price: '',
    exp: ''
  });
  if (!isOpen) return null;
  const handleAnswer = (question: keyof typeof answers, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [question]: value
    }));
    if (value === 'learn') {
      setAnswers(prev => ({
        ...prev,
        goal: 'learn'
      }));
      setStep(4);
      return;
    }
    if (step < 3) {
      setStep(step + 1);
    } else {
      setStep(4);
    }
  };
  const getRecommendation = () => {
    if (answers.goal === 'learn') return 'learn-all';
    if (answers.goal === 'list') return 'lead';
    if (answers.goal === 'product') return 'product';
    if (answers.goal === 'webinar') return 'presentation';
    if (answers.goal === 'appointments') return 'appointment';
    if (answers.goal === 'challenge') return 'challenge';
    return 'lead';
  };
  const resetAndClose = () => {
    setStep(1);
    setAnswers({
      goal: '',
      price: '',
      exp: ''
    });
    onClose();
  };
  const applyRecommendation = () => {
    onRecommend(getRecommendation());
    resetAndClose();
  };
  const isLearnMode = answers.goal === 'learn';
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-138">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-border" data-id="element-139">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-140">
          <div className="flex items-center gap-2 text-blue-600 font-semibold" data-id="element-141">
            <FontAwesomeIcon icon={faWandMagicSparkles} className="w-4 h-4" data-id="element-142" />
            Let's Find Your Template
          </div>
          <button onClick={resetAndClose} className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors" data-id="element-143">
            <XIcon className="w-5 h-5" data-id="element-144" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto" data-id="element-145">
          {step === 1 && <div className="animate-in slide-in-from-right-4 fade-in" data-id="element-146">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-147">
                What are you trying to do?
              </h2>
              <p className="text-muted-foreground mb-6" data-id="element-148">
                Pick the one that sounds most like you — there's no wrong
                answer!
              </p>

              <div className="space-y-3" data-id="element-149">
                {[{
              id: 'list',
              label: "Get people's email addresses",
              icon: faEnvelope
            }, {
              id: 'product',
              label: 'Sell something online',
              icon: faCartShopping
            }, {
              id: 'appointments',
              label: 'Get people to call me',
              icon: faPhone
            }, {
              id: 'webinar',
              label: 'Present to a group online',
              icon: faVideo
            }, {
              id: 'challenge',
              label: 'Run a fun challenge',
              icon: faTrophy
            }].map(opt => <button key={opt.id} onClick={() => handleAnswer('goal', opt.id)} className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-blue-400 hover:bg-blue-50/50 transition-all font-medium text-foreground flex justify-between items-center group" data-id="element-150">
                    <span className="flex items-center gap-3" data-id="element-151">
                      <FontAwesomeIcon icon={opt.icon} className="text-muted-foreground group-hover:text-blue-600 w-4 h-4" data-id="element-152" />
                      {opt.label}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all text-blue-600" data-id="element-153" />
                  </button>)}

                {/* Learn about everything option */}
                <div className="pt-2 border-t border-border mt-2" data-id="element-154">
                  <button onClick={() => handleAnswer('goal', 'learn')} className="w-full text-left p-4 rounded-xl border-2 border-blue-100 bg-blue-50/50 hover:border-blue-400 hover:bg-blue-50 transition-all font-medium text-blue-700 hover:text-blue-800 flex justify-between items-center group" data-id="element-155">
                    <span className="flex items-center gap-3" data-id="element-156">
                      <FontAwesomeIcon icon={faBookOpen} className="w-4 h-4" data-id="element-157" />I
                      just want to look around and learn
                    </span>
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" data-id="element-158" />
                  </button>
                </div>
              </div>
            </div>}

          {step === 2 && <div className="animate-in slide-in-from-right-4 fade-in" data-id="element-159">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-160">
                How much does your thing cost?
              </h2>
              <p className="text-muted-foreground mb-6" data-id="element-161">
                This helps us pick the right setup for you.
              </p>

              <div className="space-y-3" data-id="element-162">
                {[{
              id: 'free',
              label: "It's free!",
              icon: faTag
            }, {
              id: 'low',
              label: 'Under $50 — a quick buy',
              icon: faMoneyBill
            }, {
              id: 'mid',
              label: '$50–$500 — takes some thinking',
              icon: faSackDollar
            }, {
              id: 'high',
              label: 'Over $500 — a big decision',
              icon: faGem
            }].map(opt => <button key={opt.id} onClick={() => handleAnswer('price', opt.id)} className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-blue-400 hover:bg-blue-50/50 transition-all font-medium text-foreground flex justify-between items-center group" data-id="element-163">
                    <span className="flex items-center gap-3" data-id="element-164">
                      <FontAwesomeIcon icon={opt.icon} className="text-muted-foreground group-hover:text-blue-600 w-4 h-4" data-id="element-165" />
                      {opt.label}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all text-blue-600" data-id="element-166" />
                  </button>)}
              </div>
            </div>}

          {step === 3 && <div className="animate-in slide-in-from-right-4 fade-in" data-id="element-167">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-168">
                Have you built a website before?
              </h2>
              <p className="text-muted-foreground mb-6" data-id="element-169">
                Be honest — we won't judge!
              </p>

              <div className="space-y-3" data-id="element-170">
                {[{
              id: 'new',
              label: 'Nope, totally new to this',
              icon: faEgg
            }, {
              id: 'some',
              label: "I've made a few simple pages",
              icon: faHammer
            }, {
              id: 'pro',
              label: 'I do this all the time',
              icon: faBuilding
            }].map(opt => <button key={opt.id} onClick={() => handleAnswer('exp', opt.id)} className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-blue-400 hover:bg-blue-50/50 transition-all font-medium text-foreground flex justify-between items-center group" data-id="element-171">
                    <span className="flex items-center gap-3" data-id="element-172">
                      <FontAwesomeIcon icon={opt.icon} className="text-muted-foreground group-hover:text-blue-600 w-4 h-4" data-id="element-173" />
                      {opt.label}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all text-blue-600" data-id="element-174" />
                  </button>)}
              </div>
            </div>}

          {step === 4 && <div className="animate-in zoom-in-95 fade-in text-center py-8" data-id="element-175">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-176">
                {isLearnMode ? <FontAwesomeIcon icon={faBookOpen} className="w-8 h-8" data-id="element-177" /> : <FontAwesomeIcon icon={faWandMagicSparkles} className="w-8 h-8" data-id="element-178" />}
              </div>

              {isLearnMode ? <>
                  <h2 className="text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2" data-id="element-179">
                    Let's explore!
                    <FontAwesomeIcon icon={faStar} className="text-blue-600 w-5 h-5" data-id="element-180" />
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-sm mx-auto" data-id="element-181">
                    We'll show you all the different types so you can browse
                    around. Take your time — no rush!
                  </p>
                  <Button size="lg" className="w-full text-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={applyRecommendation} data-id="element-182">
                    Start Learning
                  </Button>
                </> : <>
                  <h2 className="text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2" data-id="element-183">
                    We found your match!
                    <FontAwesomeIcon icon={faBullseye} className="text-blue-600 w-5 h-5" data-id="element-184" />
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-sm mx-auto" data-id="element-185">
                    Based on what you told us, you should start with a{' '}
                    <strong className="text-foreground" data-id="element-186">
                      {FUNNEL_TYPES.find(t => t.id === getRecommendation())?.name.toUpperCase()}
                    </strong>{' '}
                    template. It's exactly what you need to get started.
                  </p>
                  <Button size="lg" className="w-full text-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={applyRecommendation} data-id="element-187">
                    Show Me Templates
                  </Button>
                </>}

              <button onClick={resetAndClose} className="mt-4 text-sm text-muted-foreground hover:text-foreground font-medium" data-id="element-188">
                Nah, I'll just browse everything
              </button>
            </div>}
        </div>

        {/* Progress Bar */}
        {step < 4 && <div className="bg-muted/50 p-4 border-t border-border" data-id="element-189">
            <div className="flex justify-between text-xs font-medium text-muted-foreground mb-2" data-id="element-190">
              <span data-id="element-191">Step {step} of 3</span>
              <span data-id="element-192">{Math.round(step / 3 * 100)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5" data-id="element-193">
              <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" style={{
            width: `${step / 3 * 100}%`
          }} data-id="element-194" />
            </div>
          </div>}
      </div>
    </div>;
}